import { removeGid } from '../utilities/handleGid';
import { logError } from '../utilities/logging';
import { Addon, ChoicePayload } from './addToCart';
import { validateAddon, validateChoice } from './atcValidation';

interface BuildPersonalisationChoicesOptions {
  choices: ChoicePayload[];
  addons: Addon[];
  baseProductConfigurationSlotId: string;
}

/**
 * Builds a personalisation choices array for cart items
 *
 * @param {BuildPersonalisationChoicesOptions} options - Configuration options
 * @returns {PersonalisationChoice[]} Array of personalisation choices
 * @throws {Error} If required parameters are missing or invalid
 */
export function buildPersonalisationChoices({
  choices = [],
  addons = [],
  baseProductConfigurationSlotId
}: BuildPersonalisationChoicesOptions) {
  // Input validation
  if (!Array.isArray(choices) || !Array.isArray(addons)) {
    throw new Error('Choices and addons must be arrays');
  }

  const choicesWithoutBaseProduct = choices.filter((choice) => {
    return choice.slotId !== baseProductConfigurationSlotId;
  });

  // Collect all validation errors
  const errors: string[] = [];

  // Validate all choices
  choicesWithoutBaseProduct.forEach((choice, index) => {
    const error = validateChoice(choice, index);
    if (error) {
      errors.push(...error);
    }
  });

  // Validate all addons
  addons.forEach((addon, index) => {
    const error = validateAddon(addon, index);
    if (error) {
      errors.push(error);
    }
  });

  // If there are validation errors, throw them all at once
  if (errors.length > 0) {
    throw new Error(`Validation errors:\n${errors.join('\n')}`);
  }

  try {
    // Build choices array
    const personalisationChoices = [
      // Map regular choices
      ...choicesWithoutBaseProduct
        // Filter out superficial choices.
        .filter((choice) => !['CHECKBOX', 'STAR_MAP'].includes(choice.slotType))
        .map(
          (
            {
              componentGroupId,
              variantId,
              slotId,
              lineItemPropertyKey,
              lineItemPropertyKeyValue,
              slotType,
              stepId,
              font,
              extraImages,
              visualisationGroup,
              supportingImage,
              supportingImageLineItemPropertyKey
            },
            index
          ) => {
            let choiceId = null;

            if (!['ALPHANUMERIC', 'IMAGE_UPLOAD'].includes(slotType) && (!componentGroupId || !variantId)) {
              logError({
                label: 'addToCart',
                error: new Error('Component group ID or variant ID is null'),
                message: 'Component group ID or variant ID is null',
                extraData: { componentGroupId, variantId },
                throwError: true
              });
            }

            switch (slotType) {
              case 'COMPONENT':
              case 'SEQUENTIAL':
                choiceId =
                  componentGroupId && variantId
                    ? `${removeGid({ id: componentGroupId })}:${removeGid({ id: variantId })}`
                    : null;
                break;
              case 'ALPHANUMERIC':
                choiceId = lineItemPropertyKeyValue;
                break;
              case 'IMAGE_UPLOAD':
                choiceId = lineItemPropertyKeyValue;
                break;
              default:
                throw new Error(`Unknown slot type: ${slotType}`);
            }

            return {
              choice: choiceId || '',
              slotId: removeGid({ id: slotId }),
              index: index,
              lineItemPropertyKey,
              lineItemPropertyKeyValue: lineItemPropertyKeyValue,
              slotType,
              stepId: removeGid({ id: stepId }),
              font: font,
              extraImages: extraImages || [],
              visualisationGroupId: visualisationGroup || null,
              supportingImage: supportingImage || null,
              supportingImageLineItemPropertyKey: supportingImageLineItemPropertyKey || null
            };
          }
        ),

      // Map addon choices
      ...addons.map(({ addonItemId, variantId, stepId }, index) => ({
        choice: addonItemId && variantId ? `${removeGid({ id: addonItemId })}:${removeGid({ id: variantId })}` : '',
        slotId: removeGid({ id: stepId }),
        stepId: removeGid({ id: stepId }),
        index: choices.length + index,
        lineItemPropertyKey: undefined,
        lineItemPropertyKeyValue: undefined,
        slotType: 'ADDON',
        font: undefined
      }))
    ];

    return personalisationChoices;
  } catch (error) {
    throw new Error(`Failed to build personalisation choices: ${error.message}`);
  }
}
